/**
 * Fast-save "stand-in" Service Parts rows.
 *
 * When a part is added through the write-back queue, OfficeHub saves the row at once under a temporary
 * id (`oh_...`) and AppSheet issues the real "Service Part ID" a moment later. Until the swap commits,
 * the mirror collection holds a STAND-IN: a real part the person added, not yet known to AppSheet.
 *
 * ONE definition, used by the server (invoice draft, FSR pre-fill, triggers) and the client (Item List,
 * totals, review screens), so they cannot disagree about what a stand-in is. The `_pendingCreate`
 * marker is the single source of truth; the id prefix is only a naming convention and is never read
 * as evidence.
 *
 * A stand-in is NEVER hidden from money: a reader that meets one either shows it marked "sending" or
 * refuses to build on it (an invoice draft). Silently skipping it would under-bill a real part.
 */

/** Doc-id prefix of a stand-in. Naming only; `_pendingCreate` decides. */
export const STAND_IN_ID_PREFIX = 'oh_';

/**
 * How long a stand-in may stay unconfirmed before it counts as stale and needs a person. A normal hand-off takes seconds. It is LONGER
 * than the queue's 30-minute deadline for an Add of unknown outcome (servicePartAddDrain VERIFY_DEADLINE_MS) plus a margin, so a part
 * the queue is still trying to confirm is never already labelled "needs a person" while the delete button is refused.
 */
export const STAND_IN_STALE_AFTER_MS = 45 * 60 * 1000;

/** Where a stand-in stands. `none` = a real AppSheet row. */
export type StandInState = 'none' | 'sending' | 'stale' | 'needs-attention';

function marker(doc: unknown): Record<string, unknown> | null {
  if (typeof doc !== 'object' || doc === null) return null;
  const m = (doc as Record<string, unknown>)._pendingCreate;
  return typeof m === 'object' && m !== null ? (m as Record<string, unknown>) : null;
}

/** True when the row is a fast-save stand-in (the `_pendingCreate` marker is set). */
export function isPendingStandIn(doc: unknown): boolean {
  return marker(doc) !== null;
}

/** The stand-in's state at `nowMs`. An unreadable `createdAt` is stale: fail toward a person. */
export function standInState(doc: unknown, nowMs: number): StandInState {
  const m = marker(doc);
  if (m === null) return 'none';
  if (m.needsAttention === true) return 'needs-attention';
  const created = typeof m.createdAt === 'string' ? Date.parse(m.createdAt) : NaN;
  if (!Number.isFinite(created)) return 'stale';
  return nowMs - created > STAND_IN_STALE_AFTER_MS ? 'stale' : 'sending';
}

/** True when the stand-in has outlived the limit. */
export function isStaleStandIn(doc: unknown, nowMs: number): boolean {
  return standInState(doc, nowMs) === 'stale';
}

/** The spare AppSheet column that carries a stand-in's one-time code (Service Parts "Open4"). */
const TOKEN_COLUMN = 'Open4';

/**
 * Listing readers (the FSR parts pre-fill, the Item List, the equipment page) show a stand-in: it is a real part the person added.
 * The moment that would list a part TWICE is when AppSheet's real row already exists in the mirror but the stand-in has not been swapped
 * out yet: the pull-sync can create the real row first, or the real row can arrive after the queue gave up on the stand-in. The real row
 * is recognised two ways: it carries the stand-in's id (`_standInId`, written by the swap) or the stand-in's one-time code in
 * `Open4` (stored by AppSheet with the Add and mirrored by the sync). Drop the stand-in then; keep it otherwise.
 * Money readers do not use this: they refuse while any stand-in exists (see invoices/dataCollector.ts).
 */
export function dropSupersededStandIns<T extends { id: string; data: Record<string, unknown> }>(rows: readonly T[]): T[] {
  const idByToken = new Map<string, string>();
  for (const { id, data } of rows) {
    const marker = (data as { _pendingCreate?: { addToken?: unknown } })._pendingCreate;
    const token = marker && typeof marker === 'object' ? marker.addToken : undefined;
    if (isPendingStandIn(data) && typeof token === 'string' && token) idByToken.set(token, id);
  }
  const superseded = new Set<string>();
  for (const { data } of rows) {
    if (isPendingStandIn(data)) continue;
    const twin = data._standInId;
    if (typeof twin === 'string' && twin) superseded.add(twin);
    const code = data[TOKEN_COLUMN];
    const byCode = typeof code === 'string' ? idByToken.get(code) : undefined;
    if (byCode) superseded.add(byCode);
  }
  return rows.filter(({ id, data }) => !(isPendingStandIn(data) && superseded.has(id)));
}
